import { useState, useEffect, useRef } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from '@/contexts/AuthContext'

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
const rtfNumeric = new Intl.RelativeTimeFormat('en', { numeric: 'always' })

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString // fallback for invalid dates

  const now = new Date()
  const diffMs = date.getTime() - now.getTime() // positive = future, negative = past
  const absSecs = Math.floor(Math.abs(diffMs) / 1000)
  const sign = diffMs < 0 ? -1 : 1

  // < 5 seconds: "Just now"
  if (absSecs < 5) return 'Just now'

  // < 60 seconds: "X seconds ago" (always numeric for precision)
  if (absSecs < 60) {
    const result = rtfNumeric.format(sign * absSecs, 'second')
    return result.charAt(0).toUpperCase() + result.slice(1)
  }

  // Use rounded values for a more natural feel
  const absMins = Math.round(absSecs / 60)
  const absHours = Math.round(absSecs / 3600)
  const absDays = Math.round(absSecs / 86400)

  // Future dates beyond 7 days: show actual date
  if (diffMs > 0 && absDays >= 7) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Use rounded values
  let result = '';
  if (absMins < 60) result = rtf.format(sign * absMins, 'minute')
  else if (absHours < 24) result = rtf.format(sign * absHours, 'hour')
  else if (absDays < 7) result = rtf.format(sign * absDays, 'day')
  else if (absDays < 30) result = rtf.format(sign * Math.round(absDays / 7), 'week')
  else if (absDays < 365) result = rtf.format(sign * Math.round(absDays / 30), 'month')
  else result = rtf.format(sign * Math.round(absDays / 365), 'year')

  return result.charAt(0).toUpperCase() + result.slice(1)
}

/** Decide how often to re-render based on the age of the date. */
function getRefreshInterval(dateString: string): number {
  const absSecs = Math.floor(Math.abs(Date.now() - new Date(dateString).getTime()) / 1000)
  if (absSecs < 60) return 5_000      // < 1 min  → refresh every 5s
  if (absSecs < 3600) return 30_000    // < 1 hour → refresh every 30s
  return 60_000                        // older    → refresh every 60s
}

function formatFullDate(dateString: string, timezone?: string): string {
  const date = new Date(dateString)
  
  if (!timezone) {
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (e) {
      timezone = 'UTC'
    }
  }
  
  const cityName = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone

  let utcOffset = 'UTC+00:00'
  try {
    const now = new Date()
    const local = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
    const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
    const diff = (local.getTime() - utc.getTime()) / 60000
    const abs = Math.abs(diff)
    const h = Math.floor(abs / 60)
    const m = abs % 60
    const sign = diff >= 0 ? '+' : '-'
    utcOffset = `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  } catch {
    // fallback keeping the default
  }

  const formatted = date.toLocaleDateString('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  
  return `${cityName} (${utcOffset}): ${formatted}`
}

interface RelativeTimeProps {
  date: string
  className?: string
}

export function RelativeTime({ date, className }: RelativeTimeProps) {
  const { user } = useAuth()
  const [, setTick] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Adaptive refresh: faster for recent dates, slower for older ones
  useEffect(() => {
    function schedule() {
      if (intervalRef.current) clearInterval(intervalRef.current)
      const ms = getRefreshInterval(date)
      intervalRef.current = setInterval(() => {
        setTick((t) => t + 1)
        // Re-evaluate interval in case the date has aged into a slower bucket
        schedule()
      }, ms)
    }
    schedule()
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [date])

  if (!date) return null

  return (
    <TooltipProvider delayDuration={ 200 }>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={ className } style={ { cursor: 'default' } }>
            { getRelativeTime(date) }
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{ formatFullDate(date, user?.timezone) }</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

